import useResy from 'src/hooks/useResy';
import uberLogo from '../../assets/uber-logo.png';
import resyLogo from '../../assets/resy-logo.png';
import ResyIntegrationModal from './ResyIntegrationModal';
import React, { useEffect, useState } from 'react';
import PhotoGallery from '../ActivityDrawer/PhotoGallery';
import lyftLogo from '../../assets/lyft-logo.png';
import { useSnackbar } from 'src/hooks/SnackbarProvider';
import { useSortable } from '@dnd-kit/sortable';
import { usePlan } from 'src/hooks/PlanProvider';
import { Person, ResyReservation, ResyVenue, ResyVenueTimeSlot, ConnectionState } from 'src/types/interfaces';
import { GroupAdd, ExpandMore, AccessTime, PhotoLibrary } from '@mui/icons-material';
import { formatedTime } from 'src/lib/utils';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, IconButton, Collapse, Chip, Badge, Modal, Typography } from '@mui/material';
import { ActivityRendererProps } from '../PlanStepper/PlanStepperBase';
import './PlanBuilderActivityRenderer.scss';
import ResyModal from '../Integrations/ResyModal';
import { useQueryClient } from '@tanstack/react-query';
import { APIClient, APIEndpoint } from 'src/lib/api_client';


export const PlanBuilderActivityRenderer: React.FC<ActivityRendererProps> = ({ activity, id, onUpdateActivity = () => { } }) => {
  const { triggerSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { selectedPlan, setSelectedPlanActivities, savePlan } = usePlan()
  const { setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const { reservations, status } = useResy();
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  };

  const [people, setPeople] = useState<Person[]>([])
  const [photoLibraryOpen, setPhotoLibraryOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showResyBooking, setShowResyBooking] = useState(false);
  const [showResyOnboarding, setShowResyOnboarding] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [shakingButton, setShakingButton] = useState<string | null>(null);

  const handleExpandClick = () => setIsExpanded(!isExpanded);

  const handleUnavailableFeatureClick = (buttonName: string) => {
    if (shakingButton) return;
    setShakingButton(buttonName);
    setTimeout(() => setShakingButton(null), 400); // Animation duration
  };

  const handleResyBookingComplete = (reservation: ResyReservation, venue: ResyVenue, slot: ResyVenueTimeSlot) => {
    const updatedActivities = selectedPlan.activities.map(existingActivity =>
      existingActivity.placeId === activity.placeId
        ? { ...existingActivity, resy: { reservation, venue, slot } }
        : existingActivity
    );
    setSelectedPlanActivities(updatedActivities);
    savePlan({ ...selectedPlan, activities: updatedActivities })
    triggerSnackbar(`Reservation for ${formatedTime(slot.date.start)} successful!`)
  }

  const handleResyClick = async () => {
    if (isReconnecting) return;

    if (status.data === ConnectionState.Connected) {
      setShowResyBooking(true);
    } else if (status.data === ConnectionState.Disabled) {
      setIsReconnecting(true);
      try {
        const response = await APIClient.post(`${APIEndpoint}/services/resy/reconnect/`);
        if (response.status === 200) {
          await queryClient.invalidateQueries({ queryKey: ['resyConnection'] });
          triggerSnackbar('Resy reconnected successfully! Please click again to book.', 3000, 'success');
        } else {
          throw new Error(`Reconnect failed with status ${response.status}`);
        }
      } catch (error) {
        console.error("Failed to reconnect to Resy", error);
        triggerSnackbar('Failed to reconnect Resy. Please connect from the Integrations page.', 4000, 'error');
      } finally {
        setIsReconnecting(false);
      }
    } else { // Disconnected or Unknown
      setShowResyOnboarding(true);
    }
  };

  const handleFriendsClick = async () => {
    setPeople([...people, {} as Person])
    console.debug("TODO: implement handleFriendsClick()")
  };

  useEffect(() => {
    if (reservations.data) {
      console.debug("TODO: implement Resy reservation state reconciliation:", reservations.data);
    }
  }, [reservations])

  return (
    <>
      <Modal open={photoLibraryOpen} onClose={() => setPhotoLibraryOpen(false)}>
        <PhotoGallery photos={activity.photos} classNames={['activity-card-photo']} />
      </Modal>
      <ResyModal
        open={showResyOnboarding}
        connectionState={status.data || ConnectionState.Unknown}
        onClose={() => setShowResyOnboarding(false)}
      />
      <ResyIntegrationModal
        open={showResyBooking}
        activity={activity}
        onClose={() => setShowResyBooking(false)}
        onBookingComplete={handleResyBookingComplete}
      />
      <div ref={setNodeRef} style={style} data-dnd-dragging={isDragging}>
        <Card className={`activity-card ${isExpanded ? 'expanded' : ''}`}>
          <CardHeader
            className="activity-card-header"
            title={
              <div className="activity-card-content">
                {activity.startTime && (
                  <div className="time-chip">
                    <Chip icon={<AccessTime />} label={activity.startTime} variant="outlined" color="primary" />
                  </div>
                )}
                <div className="activity-header-buttons">
                  <IconButton
                    onClick={activity.resyId ? handleResyClick : () => handleUnavailableFeatureClick('resy')}
                    className={`${!activity.resyId ? 'unavailable' : ''} ${shakingButton === 'resy' ? 'shake' : ''}`}
                    disabled={isReconnecting}
                  >
                    {activity.resy?.reservation?.reservationId ? (
                      <Badge className="activity-badge-complete" badgeContent={"✓"} color="secondary" overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                        <img src={resyLogo} alt="Resy" />
                      </Badge>
                    ) : (
                      <Badge className="activity-badge" badgeContent={"+"} color="primary" overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                        <img src={resyLogo} alt="Resy" />
                      </Badge>
                    )}
                  </IconButton>
                  <IconButton
                    onClick={() => handleUnavailableFeatureClick('uber')}
                    className={`unavailable ${shakingButton === 'uber' ? 'shake' : ''}`}
                  >
                    <Badge className="activity-badge" badgeContent={"+"} color="primary" overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                      <img src={uberLogo} alt="Uber" />
                    </Badge>
                  </IconButton>
                  <IconButton
                    onClick={() => handleUnavailableFeatureClick('lyft')}
                    className={`unavailable ${shakingButton === 'lyft' ? 'shake' : ''}`}
                  >
                    <Badge className="activity-badge" badgeContent={"+"} color="primary" overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                      <img src={lyftLogo} alt="Lyft" />
                    </Badge>
                  </IconButton>
                </div>
              </div>
            }
            action={
              <>
                <IconButton size="large" className="friends-button" onClick={handleFriendsClick}>
                  <Badge className="activity-badge" badgeContent={people.length || 0} color="primary" overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}><GroupAdd /></Badge>
                </IconButton>
                <IconButton onClick={() => setPhotoLibraryOpen(true)} size="small" ><PhotoLibrary /></IconButton>
                {activity.resy?.reservation.reservationId && (
                  <IconButton onClick={handleExpandClick} className={`expand-more ${isExpanded ? 'expanded' : ''}`} size="small" ><ExpandMore /></IconButton>
                )}
              </>
            }
          />
          {activity.resy?.reservation.reservationId && (
            <CardContent className="activity-card-content">
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Typography>Reservation: {formatedTime(activity.resy.slot.date.start)}</Typography>
                <Typography>Party: {activity.resy.slot.size.max}</Typography>
              </Collapse>
            </CardContent>
          )}
        </Card>
      </div>
    </>
  );
};
